import { Fragment, useState, useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import IrrfFolhaConciliacaoService, { IIrrfFolhaAnaliseConta } from '../../services/IrrfFolhaConciliacaoService';

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    onHide: () => void;
    esteiraId: number;
}

const STATUS_COR: Record<string, string> = {
    PENDENTE: '#f0ad4e',
    RECOLHIDODATA: '#28a745',
    RECOLHIDOMAIOR: '#0d6efd',
    RECOLHIDOMENOR: '#dc3545',
};

const FAIXA_SCORE_COR: Record<string, string> = {
    MATCH_FORTE: 'bg-success',
    MATCH_PROVAVEL: 'bg-primary',
    MATCH_FRACO: 'bg-warning text-dark',
    NAO_CONCILIAR_AUTOMATICAMENTE: 'bg-danger',
};

const EVENTO_ICONE: Record<string, string> = {
    GRUPO_FORMADO: 'fa-link',
    COMBINACAO_AMBIGUA_DESCARTADA: 'fa-triangle-exclamation',
    REGIME_IDENTIFICADO: 'fa-calendar-check',
    REPROCESSAMENTO_DISPARADO: 'fa-rotate',
    SUGESTAO_IA_APLICADA: 'fa-robot',
    SUGESTAO_IA_REJEITADA: 'fa-robot',
    PADRAO_RUBRICA_IDENTIFICADO: 'fa-tags',
};

const RUBRICA_COR: Record<string, string> = {
    SALARIO_NORMAL: '#5E6397',
    ADIANTAMENTO: '#0d6efd',
    FERIAS: '#20c997',
    DECIMO_TERCEIRO: '#fd7e14',
    RESCISAO: '#dc3545',
};

const RUBRICA_ICONE: Record<string, string> = {
    SALARIO_NORMAL: 'fa-money-bill-wave',
    ADIANTAMENTO: 'fa-hand-holding-dollar',
    FERIAS: 'fa-umbrella-beach',
    DECIMO_TERCEIRO: 'fa-gift',
    RESCISAO: 'fa-file-circle-xmark',
};

interface IRubricaDetalhe {
    tipo: string;
    quantidade: number;
    diaMaisComum: number;
}

const formatVal = (v: number) => v.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
const formatPct = (v: number | null) => v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;
const formatData = (v: string | null) => v ? moment(v).format('DD/MM/YYYY') : '—';

const formatValorDetalhe = (v: unknown): string => {
    if (Array.isArray(v)) {
        if (v.length > 0 && typeof v[0] === 'object' && v[0] !== null) {
            // ex.: rubricas: [{ tipo, quantidade, diaMaisComum }]
            return v.map(item => Object.entries(item as object).map(([k, val]) => `${k}=${val}`).join(' ')).join('; ');
        }
        return `[${v.join(', ')}]`;
    }
    return String(v);
};

const formatDetalhe = (detalhe: string): string => {
    try {
        const obj = JSON.parse(detalhe);
        return Object.entries(obj)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => `${k}: ${formatValorDetalhe(v)}`)
            .join(' · ');
    } catch {
        return detalhe;
    }
};

const ModalIrrfFolhaAnalise = ({ show, onHide, esteiraId }: Props) => {
    const { t } = useTranslation();
    const [aba, setAba] = useState<'decisoes' | 'eventos'>('decisoes');
    const [loading, setLoading] = useState(false);
    const [analise, setAnalise] = useState<IIrrfFolhaAnaliseConta | null>(null);
    const [competenciaExpandida, setCompetenciaExpandida] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!esteiraId) return;
        setLoading(true);
        try {
            const resp = await IrrfFolhaConciliacaoService.buscarAnalise(esteiraId);
            setAnalise(resp);
        } finally {
            setLoading(false);
        }
    }, [esteiraId]);

    useEffect(() => {
        if (show) {
            setAba('decisoes');
            load();
        }
    }, [show, load]);

    const totalPorStatusMax = analise ? Math.max(...analise.porStatus.map(s => s.quantidade), 1) : 1;

    const buscarRubricas = (mes: number, ano: number): IRubricaDetalhe[] => {
        if (!analise) return [];
        const evento = analise.eventos.find(e =>
            e.tipoEvento === 'PADRAO_RUBRICA_IDENTIFICADO' && e.mesCompetencia === mes && e.anoCompetencia === ano);
        if (!evento) return [];
        try {
            const obj = JSON.parse(evento.detalhe) as { rubricas?: IRubricaDetalhe[] };
            return (obj.rubricas ?? []).slice().sort((a, b) => b.quantidade - a.quantidade);
        } catch {
            return [];
        }
    };

    return (
        <Modal fullscreen scrollable show={show} onHide={onHide}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header border-0 pb-1">
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ textTransform: 'uppercase', color: '#5E6397' }}>
                            {t('Análise de Conciliação — IRRF sobre Folha')}
                        </h5>
                        {analise && (
                            <small className="text-muted">
                                {analise.contaEmpresa} — {analise.descricaoEmpresa ?? t('sem descrição')}
                            </small>
                        )}
                        {analise && (
                            <div className="mt-1">
                                {analise.contaSalarioDetectada ? (
                                    <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.7rem' }} title={t('Datas de pagamento do IRRF são conferidas contra o pagamento real de salário desta conta')}>
                                        <i className="fas fa-anchor me-1" />{t('Âncora de conta de salário')}: {analise.contaSalarioDetectada}
                                    </span>
                                ) : (
                                    <span className="badge bg-secondary bg-opacity-10 text-secondary border" style={{ fontSize: '0.7rem' }} title={t('Nenhuma conta de "Salários e Remunerações a Pagar" encontrada nesta esteira — usando janela de recolhimento genérica')}>
                                        <i className="fas fa-calendar me-1" />{t('Sem conta de salário — janela genérica')}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onHide}>
                        <i className="fas fa-times" style={{ color: '#5E6397' }} />
                    </button>
                </div>

                {loading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <span className="spinner-border spinner-border-sm me-2 text-primary" />
                        <span className="text-muted">{t('Carregando...')}</span>
                    </div>
                )}

                {!loading && !analise && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fas fa-circle-exclamation fa-3x mb-3" style={{ color: '#ccc' }} />
                        <p className="mb-0">{t('Não foi possível carregar a análise desta conta.')}</p>
                    </div>
                )}

                {!loading && analise && (
                    <>
                        {/* Resumo em cards */}
                        <div className="px-4 pt-3" style={{ backgroundColor: '#fff' }}>
                            <div className="row g-3 pb-3">
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#5E6397' }}>{analise.porcentagemConciliado}%</div>
                                        <small className="text-muted text-uppercase">{t('Conciliado')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#5E6397' }}>{analise.totalLancamentos}</div>
                                        <small className="text-muted text-uppercase">{t('Total de lançamentos')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#28a745' }}>{analise.totalConciliados}</div>
                                        <small className="text-muted text-uppercase">{t('Conciliados')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.6rem', color: '#f0ad4e' }}>{analise.totalPendentes}</div>
                                        <small className="text-muted text-uppercase">{t('Pendentes')} · {formatVal(analise.valorPendente)}</small>
                                    </div>
                                </div>
                            </div>

                            {/* Distribuição por status */}
                            <div className="pb-3">
                                <small className="text-muted text-uppercase fw-bold">{t('Distribuição por status')}</small>
                                <div className="mt-2 d-flex flex-column gap-2">
                                    {analise.porStatus.map(s => (
                                        <div key={s.status} className="d-flex align-items-center gap-2">
                                            <span style={{ width: 130, fontSize: '0.8rem', color: STATUS_COR[s.status] ?? '#666' }} className="fw-semibold text-nowrap">
                                                {s.status}
                                            </span>
                                            <div className="flex-grow-1 bg-light rounded" style={{ height: 10 }}>
                                                <div
                                                    style={{
                                                        width: `${(s.quantidade / totalPorStatusMax) * 100}%`,
                                                        height: 10,
                                                        borderRadius: 5,
                                                        backgroundColor: STATUS_COR[s.status] ?? '#999',
                                                    }}
                                                />
                                            </div>
                                            <span style={{ width: 150, fontSize: '0.78rem' }} className="text-muted text-nowrap text-end">
                                                {s.quantidade} · {formatVal(s.valor)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="px-4 pt-2 d-flex gap-2" style={{ backgroundColor: '#fff' }}>
                            <button className={`btn btn-sm ${aba === 'decisoes' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('decisoes')}>
                                {t('Decisões por competência')} <span className="badge bg-secondary ms-1">{analise.decisoes.length}</span>
                            </button>
                            <button className={`btn btn-sm ${aba === 'eventos' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('eventos')}>
                                {t('Trilha de auditoria')} <span className="badge bg-secondary ms-1">{analise.eventos.length}</span>
                            </button>
                        </div>

                        {aba === 'decisoes' && (
                            <div className="modal-body p-3">
                                {analise.decisoes.length === 0 ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                        <i className="fas fa-list fa-3x mb-3" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhuma decisão registrada ainda.')}</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover table-sm align-middle mb-0">
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                                    <th className="ps-2">{t('Competência')}</th>
                                                    <th>{t('Modalidade')}</th>
                                                    <th>{t('Status funcional')}</th>
                                                    <th className="text-center">{t('Provisões')}</th>
                                                    <th className="text-center">{t('Baixas')}</th>
                                                    <th className="text-center">{t('Divergência')}</th>
                                                    <th className="text-center">{t('Âncora salário')}</th>
                                                    <th className="text-center">{t('Score')}</th>
                                                    <th>{t('Reason codes')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analise.decisoes.map(d => {
                                                    const chave = `${d.anoCompetencia}-${d.mesCompetencia}`;
                                                    const expandida = competenciaExpandida === chave;
                                                    const rubricas = expandida ? buscarRubricas(d.mesCompetencia, d.anoCompetencia) : [];
                                                    const totalRubricas = rubricas.reduce((acc, r) => acc + r.quantidade, 0) || 1;
                                                    return (
                                                        <Fragment key={d.id}>
                                                            <tr>
                                                                <td
                                                                    className="ps-2 text-nowrap"
                                                                    style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#5E6397', fontWeight: 600 }}
                                                                    onClick={() => setCompetenciaExpandida(expandida ? null : chave)}
                                                                    title={t('Ver classificação dos lançamentos desta competência')}
                                                                >
                                                                    <i className={`fas ${expandida ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ fontSize: '0.65rem' }} />
                                                                    {String(d.mesCompetencia).padStart(2, '0')}/{d.anoCompetencia}
                                                                </td>
                                                                <td style={{ fontSize: '0.82rem' }}>{d.modalidade.replace('RECOLHIMENTO_', '')}</td>
                                                                <td style={{ fontSize: '0.82rem' }}>{d.statusFuncional.replace('CONCILIADO_', '')}</td>
                                                                <td className="text-center" style={{ fontSize: '0.85rem' }}>{d.quantidadeProvisoes}</td>
                                                                <td className="text-center" style={{ fontSize: '0.85rem' }}>{d.quantidadeBaixas}</td>
                                                                <td className="text-center fw-semibold" style={{ fontSize: '0.85rem', color: d.percentualDivergencia == null ? '#999' : (d.percentualDivergencia >= 0 ? '#0d6efd' : '#dc3545') }}>
                                                                    {formatPct(d.percentualDivergencia)}
                                                                </td>
                                                                <td className="text-center" style={{ fontSize: '0.8rem', color: d.dataAncoraSalario ? '#198754' : '#999' }}>
                                                                    {d.dataAncoraSalario ? <><i className="fas fa-anchor me-1" />{formatData(d.dataAncoraSalario)}</> : '—'}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className={`badge ${FAIXA_SCORE_COR[d.faixaScore] ?? 'bg-secondary'}`} style={{ fontSize: '0.7rem' }} title={d.faixaScore}>
                                                                        {d.score.toFixed(0)}
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontSize: '0.72rem' }}>
                                                                    {d.reasonCodes.map(rc => (
                                                                        <span key={rc} className="badge bg-light text-dark border me-1 mb-1" style={{ fontSize: '0.65rem' }}>
                                                                            {rc.replace(/_/g, ' ')}
                                                                        </span>
                                                                    ))}
                                                                </td>
                                                            </tr>
                                                            {expandida && (
                                                                <tr>
                                                                    <td colSpan={9} className="p-0" style={{ backgroundColor: '#F4F5F7' }}>
                                                                        <div className="p-3">
                                                                            <small className="text-muted text-uppercase fw-bold d-block mb-2">
                                                                                <i className="fas fa-tags me-1" />
                                                                                {t('Classificação dos lançamentos desta competência')}
                                                                            </small>
                                                                            {rubricas.length === 0 ? (
                                                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                                    {t('Nenhum padrão de rubrica registrado para esta competência.')}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="row g-2">
                                                                                    {rubricas.map(r => (
                                                                                        <div key={r.tipo} className="col-md-3 col-sm-6">
                                                                                            <div className="border rounded p-2 bg-white h-100">
                                                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                                                    <i
                                                                                                        className={`fas ${RUBRICA_ICONE[r.tipo] ?? 'fa-tag'}`}
                                                                                                        style={{ color: RUBRICA_COR[r.tipo] ?? '#6c757d' }}
                                                                                                    />
                                                                                                    <span className="fw-semibold" style={{ fontSize: '0.78rem' }}>
                                                                                                        {r.tipo.replace(/_/g, ' ')}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="bg-light rounded mb-1" style={{ height: 6 }}>
                                                                                                    <div
                                                                                                        style={{
                                                                                                            width: `${(r.quantidade / totalRubricas) * 100}%`,
                                                                                                            height: 6,
                                                                                                            borderRadius: 3,
                                                                                                            backgroundColor: RUBRICA_COR[r.tipo] ?? '#6c757d',
                                                                                                        }}
                                                                                                    />
                                                                                                </div>
                                                                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                                                                    {t('Qtd')}: {r.quantidade} · {t('dia mais comum')}: {r.diaMaisComum}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {aba === 'eventos' && (
                            <div className="modal-body p-3">
                                {analise.eventos.length === 0 ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                        <i className="fas fa-clock-rotate-left fa-3x mb-3" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhum evento de auditoria registrado ainda.')}</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {analise.eventos.map(e => (
                                            <div key={e.id} className="d-flex align-items-start gap-2 border-bottom pb-2">
                                                <i className={`fas ${EVENTO_ICONE[e.tipoEvento] ?? 'fa-circle-info'} mt-1`} style={{ color: '#5E6397', width: 16 }} />
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>{e.tipoEvento.replace(/_/g, ' ')}</span>
                                                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                            {String(e.mesCompetencia).padStart(2, '0')}/{e.anoCompetencia} · {moment(e.dataCriacao).format('DD/MM/YYYY HH:mm')}
                                                        </span>
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: '0.78rem', wordBreak: 'break-word' }}>
                                                        {formatDetalhe(e.detalhe)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalIrrfFolhaAnalise;
